import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Download, FileText, Clock, Calendar, Loader2 } from 'lucide-react'
import { voiceSessionApi, recordingApi } from '../../services/api'
import { buildDownloadName } from '../../utils/recordingFilename'

/**
 * Shape of a CallRecording row as returned by GET /api/recordings.
 * See apps/server/src/modules/spaces/routes/recordings.ts (recordingInclude).
 */
export interface RecordingRow {
  id: string
  channelId: string
  startedById: string
  status: string // STARTING | ACTIVE | STOPPING | COMPLETED | FAILED
  startedAt: string
  endedAt: string | null
  duration: number
  fileUrl: string | null
  fileSize: number | null
  format: string
  errorMessage: string | null
  startedBy?: { id: string; username: string; avatarUrl?: string | null } | null
  channel?: {
    name: string
    server?: { name: string } | null
    space?: { name: string } | null
  } | null
}

interface TranscriptEntry {
  id: string
  speakerName: string
  text: string
  cleanedText?: string | null
  createdAt: string
}

interface TranscriptSession {
  id: string
  channelId: string
  startedAt: string
  endedAt: string | null
  transcripts: TranscriptEntry[]
}

function formatDate(s: string) {
  return new Date(s).toLocaleString()
}

function formatDuration(seconds: number, startedAt: string, endedAt: string | null) {
  // Prefer DB-stored seconds (filled on egress completion). Fall back to
  // wall-clock between start/end so STOPPING / legacy rows still show
  // something sensible.
  let total = seconds
  if (!total && endedAt) {
    total = Math.max(0, Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000))
  }
  if (!total) return '—'
  const m = Math.floor(total / 60)
  const s = total % 60
  if (m === 0) return `${s}s`
  return `${m}m ${s}s`
}

function formatBytes(bytes: number | null) {
  if (!bytes) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`
}

function statusPill(status: string) {
  const map: Record<string, string> = {
    COMPLETED: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    ACTIVE: 'bg-rose-500/15 text-rose-300 ring-rose-500/30',
    STARTING: 'bg-yellow-500/15 text-yellow-300 ring-yellow-500/30',
    STOPPING: 'bg-yellow-500/15 text-yellow-300 ring-yellow-500/30',
    FAILED: 'bg-red-500/15 text-red-300 ring-red-500/30',
  }
  return map[status] ?? 'bg-muted text-muted-foreground ring-border'
}

export default function RecordingItem({ recording }: { recording: RecordingRow }) {
  const [expanded, setExpanded] = useState(false)
  const [showPlayer, setShowPlayer] = useState(false)
  const [transcripts, setTranscripts] = useState<TranscriptEntry[] | null>(null)
  const [loadingTranscripts, setLoadingTranscripts] = useState(false)

  // Access-gated stream URL (carries the JWT via ?token). Recordings are no
  // longer served from the public /uploads path.
  const href = recording.fileUrl ? recordingApi.streamUrl(recording.id) : null
  const playable = recording.status === 'COMPLETED' && !!href

  // When the user expands the card, fetch transcript sessions for the
  // recording's channel and pick the session whose startedAt window
  // overlaps this recording. Cheap (cached server-side, max 50 sessions).
  useEffect(() => {
    if (!expanded || transcripts !== null) return
    setLoadingTranscripts(true)
    voiceSessionApi.transcriptSessions(recording.channelId)
      .then((res) => {
        const sessions: TranscriptSession[] = res.data || []
        const recStart = new Date(recording.startedAt).getTime()
        const recEnd = recording.endedAt ? new Date(recording.endedAt).getTime() : Date.now()
        // A transcript session "matches" this recording if it started
        // inside the recording window (with a 60s slack on either side
        // to account for clock skew between live transcription and
        // egress start).
        const slack = 60_000
        const match = sessions.find((s) => {
          const t = new Date(s.startedAt).getTime()
          return t >= recStart - slack && t <= recEnd + slack
        })
        setTranscripts(match?.transcripts ?? [])
      })
      .catch(() => setTranscripts([]))
      .finally(() => setLoadingTranscripts(false))
  }, [expanded, recording.channelId, recording.startedAt, recording.endedAt, transcripts])

  const channelLabel = recording.channel?.name
    ? `${recording.channel.server?.name ?? recording.channel.space?.name ?? ''} #${recording.channel.name}`.trim()
    : `Channel ${recording.channelId.slice(0, 8)}`

  return (
    <div className="rounded-xl bg-card ring-1 ring-border p-4">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 text-left flex items-start gap-3"
        >
          <span className="mt-1 text-muted-foreground">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-foreground font-medium truncate">{channelLabel}</span>
              <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ring-1 ${statusPill(recording.status)}`}>
                {recording.status}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              <span className="inline-flex items-center gap-1"><Calendar size={12} /> {formatDate(recording.startedAt)}</span>
              <span className="inline-flex items-center gap-1"><Clock size={12} /> {formatDuration(recording.duration, recording.startedAt, recording.endedAt)}</span>
              {recording.fileSize ? <span>{formatBytes(recording.fileSize)}</span> : null}
              {recording.startedBy?.username ? <span>by {recording.startedBy.username}</span> : null}
            </div>
          </div>
        </button>

        <div className="flex items-center gap-2 shrink-0">
          {playable ? (
            <>
              <button
                type="button"
                onClick={() => setShowPlayer((v) => !v)}
                className="px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition duration-150"
              >
                {showPlayer ? 'Hide player' : 'Play'}
              </button>
              {href ? (
                <a
                  href={href}
                  download={buildDownloadName(recording)}
                  className="p-1.5 rounded-md text-foreground hover:text-foreground hover:bg-accent transition duration-150"
                  title="Download"
                >
                  <Download size={14} />
                </a>
              ) : null}
            </>
          ) : (
            <span className="text-xs text-muted-foreground">{recording.status === 'FAILED' ? 'Unavailable' : 'Processing…'}</span>
          )}
        </div>
      </div>

      {showPlayer && href ? (
        <div className="mt-3">
          <audio src={href} controls preload="metadata" className="w-full" />
        </div>
      ) : null}

      {expanded ? (
        <div className="mt-3 border-t border-border pt-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <FileText size={12} />
            <span>Transcript</span>
          </div>
          {loadingTranscripts ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 size={12} className="animate-spin" /> Loading transcript…
            </div>
          ) : !transcripts || transcripts.length === 0 ? (
            <p className="text-xs text-muted-foreground">No transcript captured for this recording.</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {transcripts.map((t) => (
                <div key={t.id} className="text-sm leading-snug">
                  <span className="text-amber-500 font-medium">{t.speakerName}:</span>{' '}
                  <span className="text-foreground">{t.cleanedText ?? t.text}</span>
                </div>
              ))}
            </div>
          )}
          {recording.errorMessage ? (
            <p className="mt-2 text-xs text-rose-300">Error: {recording.errorMessage}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
